import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Trash2, Ban, AlertTriangle } from "lucide-react";
import { easternInputToISO, formatEastern } from "@/lib/utils";

type Studio = { id: string; name: string };
type Block = {
  id: string;
  studio_id: string | null;
  start_at: string;
  end_at: string;
  reason: string | null;
  source: string;
  created_at: string;
};

const ALL = "__all__";

/** Location presets so staff can block a city without touching code. */
const CITY_PRESETS = [
  { value: "columbus", label: "Columbus, GA" },
  { value: "atlanta", label: "Atlanta, GA" },
] as const;

const AdminBlocks = () => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [studioId, setStudioId] = useState<string>(ALL);
  const [city, setCity] = useState<string>("columbus");
  const [kind, setKind] = useState<"blocked" | "limited">("blocked");
  const [date, setDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("21:00");
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    const [{ data: st }, { data: bl }] = await Promise.all([
      supabase.from("studios").select("id,name").eq("active", true).order("display_order"),
      supabase
        .from("calendar_blocks")
        .select("id,studio_id,start_at,end_at,reason,source,created_at")
        .gte("end_at", new Date(Date.now() - 7 * 86400000).toISOString())
        .order("start_at", { ascending: true })
        .limit(300),
    ]);
    setStudios((st ?? []) as Studio[]);
    setBlocks((bl ?? []) as Block[]);
    setLoading(false);
  };

  useEffect(() => {
    document.title = "Blocked Dates | Admin";
    load();
  }, []);

  const studioName = useMemo(
    () => (id: string | null) => (id ? studios.find((s) => s.id === id)?.name ?? "Unknown studio" : "All studios"),
    [studios],
  );

  const create = async () => {
    if (endDate < date) { toast.error("End date is before the start date."); return; }
    if (date === endDate && endTime <= startTime) { toast.error("End time must be after the start time."); return; }
    setSaving(true);
    const cityName = CITY_PRESETS.find((c) => c.value === city)?.label ?? city;
    const reason = [
      kind === "limited" ? "Limited availability" : "Blocked",
      cityName,
      note.trim() || null,
    ].filter(Boolean).join(" · ");

    // One row per day so a multi-day range only blocks the chosen hours each day.
    const rows: { studio_id: string | null; start_at: string; end_at: string; reason: string; source: string }[] = [];
    for (let d = new Date(`${date}T00:00:00`); d <= new Date(`${endDate}T00:00:00`); d.setDate(d.getDate() + 1)) {
      const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      rows.push({
        studio_id: studioId === ALL ? null : studioId,
        start_at: easternInputToISO(`${day}T${startTime}`),
        end_at: easternInputToISO(`${day}T${endTime}`),
        reason,
        source: kind === "limited" ? "admin_limited" : "admin",
      });
    }

    const { error } = await supabase.from("calendar_blocks").insert(rows);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`${rows.length} ${rows.length === 1 ? "block" : "blocks"} added — availability updated`);
    setNote("");
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("calendar_blocks").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    setBlocks((b) => b.filter((x) => x.id !== id));
    toast.success("Block removed — slot is bookable again");
  };

  return (
    <AdminLayout title="BLOCKED DATES">
      <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">
        Close off dates and time ranges per studio or city. Public availability updates immediately.
      </p>

      <div className="bg-panel border border-border p-6 mb-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">Studio</Label>
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All studios</SelectItem>
              {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">Location</Label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CITY_PRESETS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">Type</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as "blocked" | "limited")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="blocked">Blocked (unbookable)</SelectItem>
              <SelectItem value="limited">Limited (block these hours only)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">From date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">To date</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Start (ET)</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">End (ET)</Label>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="font-mono text-xs uppercase tracking-widest">Note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Maintenance, private event, holiday…" />
        </div>
        <div className="flex items-end">
          <Button onClick={create} disabled={saving} className="w-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-2" />Block these hours</>}
          </Button>
        </div>
      </div>

      <h2 className="font-display text-sm tracking-widest mb-3">UPCOMING BLOCKS</h2>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="bg-panel border border-border divide-y divide-border">
          {blocks.length === 0 && (
            <div className="p-4 text-xs font-mono text-muted-foreground">No blocks — every published hour is bookable.</div>
          )}
          {blocks.map((b) => (
            <div key={b.id} className="p-3 flex items-center justify-between gap-3 text-xs font-mono">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {b.source === "admin_limited"
                    ? <AlertTriangle className="w-3 h-3 text-primary shrink-0" />
                    : <Ban className="w-3 h-3 text-destructive shrink-0" />}
                  <span className="uppercase tracking-widest">{studioName(b.studio_id)}</span>
                </div>
                <div className="text-muted-foreground mt-1 truncate">
                  {formatEastern(b.start_at)} → {formatEastern(b.end_at)}
                </div>
                {b.reason && <div className="text-muted-foreground/70 mt-1 truncate">{b.reason}</div>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(b.id)} aria-label="Remove block">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBlocks;
